import React, { useState, useEffect } from "react"
import { Modal, Button, Form, Row, Col } from "react-bootstrap"
import { IEmpleados, AddEmpleadoModalProps } from "../../interfaces/IEmpleados"
import { Auth0User } from "../../interfaces/Auth0"
import { useCreateUserAuth0 } from "../Auth0/Hooks/useCreateUserAuth0"
import { useAssignRoleToUserAuth0 } from "../Auth0/Hooks/useAssignRoleToUserAuth0"
import { useApi } from "../../hooks/useApi"
import { API_ENDPOINTS, AUTH0_CONFIG } from "../../api/endpoints"
import {
  validateEmail,
  validatePassword,
  isPasswordValid,
} from "../../utils/validation"

const AddEmpleadoModal: React.FC<AddEmpleadoModalProps> = ({
  show,
  handleClose,
  handleEmpleadoAdd,
}) => {
  const [nombre, setNombre] = useState("")
  const [apellido, setApellido] = useState("")
  const [email, setEmail] = useState("")
  const [clave, setClave] = useState("")
  const [estado, setEstado] = useState(false)
  const [emailValido, setEmailValido] = useState(true)
  const [emailEnUso, setEmailEnUso] = useState(false)
  const API_URL = API_ENDPOINTS.EMPLEADOS
  const ID_ROL = AUTH0_CONFIG.ID_ROL_EMPL
  const api = useApi()
  const [passwordValidationState, setPasswordValidationState] = useState(
    validatePassword(""),
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [claveValida, setClaveValida] = useState(true)

  const createUserAuth0 = useCreateUserAuth0()
  const assignRoleToUserAuth0 = useAssignRoleToUserAuth0()

  useEffect(() => {
    setPasswordValidationState(validatePassword(clave))
  }, [clave])

  // Esta función restablecerá los valores del formulario a su estado inicial
  const resetForm = () => {
    setNombre("")
    setApellido("")
    setEmail("")
    setClave("")
    setEstado(false)
    setEmailEnUso(false)
    setIsSubmitting(false)
    setClaveValida(true)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    try {
      const empleados = await api.get<IEmpleados[]>(API_URL, {
        errorMessage: "Error al verificar el email",
      })
      if (empleados.some((empleado) => empleado.email === email)) {
        setEmailEnUso(true)
        return
      }
      setEmailEnUso(false)
    } catch {
      return
    }

    if (!validateEmail(email)) {
      setEmailValido(false)
      return
    }
    setEmailValido(true)

    if (!isPasswordValid(passwordValidationState)) {
      setClaveValida(false)
      return
    }

    setIsSubmitting(true)

    const customerUser: Auth0User = {
      username: nombre,
      email,
      password: clave,
      blocked: estado,
    }

    try {
      const newAuth0UserId = await createUserAuth0(customerUser)
      if (!newAuth0UserId) {
        setIsSubmitting(false)
        return
      }
      await assignRoleToUserAuth0(newAuth0UserId, ID_ROL)
      handleEmpleadoAdd({
        id: 0,
        nombre,
        apellido,
        email,
        clave,
        estado,
        idAuth0: newAuth0UserId,
      })
      handleClose()
      resetForm()
    } catch {
      setIsSubmitting(false)
    }
  }

  const handleCancelar = () => {
    resetForm()
    handleClose()
  }

  const handleCloseModal = () => {
    resetForm()
    handleClose()
  }

  return (
    <Modal show={show} onHide={handleCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>Agregar Empleado</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formNombre">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese nombre"
                  value={nombre}
                  onChange={(event) => setNombre(event.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formApellido">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese apellido"
                  value={apellido}
                  onChange={(event) => setApellido(event.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  isInvalid={!emailValido || emailEnUso}
                />
                {!emailValido && (
                  <Form.Control.Feedback type="invalid">
                    Email no válido.
                  </Form.Control.Feedback>
                )}
                {emailEnUso && (
                  <Form.Control.Feedback type="invalid">
                    Este email ya está en uso.
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formEstado">
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={estado ? "activo" : "bloqueado"}
                  onChange={(event) =>
                    setEstado(event.target.value === "bloqueado" ? false : true)
                  }
                  required
                >
                  <option value="bloqueado">Activo</option>
                  <option value="activo">Bloqueado</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md="5">
              <Form.Group className="mb-3" controlId="formClave">
                <Form.Label>Contraseña</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Contraseña"
                  value={clave}
                  onChange={(event) => setClave(event.target.value)}
                  required
                  isInvalid={!claveValida}
                />
                {!claveValida && (
                  <Form.Control.Feedback type="invalid">
                    Contraseña no válida.
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md="7">
              {clave && (
                <div className="card border-info shadow">
                  <ul className="list-unstyled">
                    <li
                      className={`password-validation-item ${
                        passwordValidationState.length
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {passwordValidationState.length ? (
                        <i className="bi bi-check"></i>
                      ) : (
                        <i className="bi bi-x"></i>
                      )}
                      Al menos 8 caracteres de largo
                    </li>
                    <li
                      className={`password-validation-item ${
                        passwordValidationState.lowercase
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {passwordValidationState.lowercase ? (
                        <i className="bi bi-check"></i>
                      ) : (
                        <i className="bi bi-x"></i>
                      )}
                      Letras minúsculas (a-z)
                    </li>
                    <li
                      className={`password-validation-item ${
                        passwordValidationState.uppercase
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {passwordValidationState.uppercase ? (
                        <i className="bi bi-check"></i>
                      ) : (
                        <i className="bi bi-x"></i>
                      )}
                      Letras mayúsculas (A-Z)
                    </li>
                    <li
                      className={`password-validation-item ${
                        passwordValidationState.number
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {passwordValidationState.number ? (
                        <i className="bi bi-check"></i>
                      ) : (
                        <i className="bi bi-x"></i>
                      )}
                      Números (0-9)
                    </li>
                    <li
                      className={`password-validation-item ${
                        passwordValidationState.specialChar
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {passwordValidationState.specialChar ? (
                        <i className="bi bi-check"></i>
                      ) : (
                        <i className="bi bi-x"></i>
                      )}
                      Caracteres especiales <br /> (por ejemplo: -_@$!%*?&)
                    </li>
                  </ul>
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            Agregar
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default AddEmpleadoModal
